// App configuration. The raw values come from BUN_PUBLIC_* environment variables (see .env.example), delivered
// to the browser by the `virtual:app-config` module that build/config-plugin.ts generates.

import type { RawConfig } from '../build/config-plugin';

export interface AppConfig {
  /** The backend's URL, no trailing slash. Empty means demo mode (sample data, no backend, no sign-in). */
  apiBaseUrl: string;
  msal: { clientId: string; authority: string };
  /** The API scope the sign-in asks for, e.g. api://<client-id>/access_as_user. */
  apiScope: string;
  demoSeedUrl?: string;
}

export function resolveConfig(raw: RawConfig): AppConfig {
  const clientId = raw.msalClientId;
  return {
    apiBaseUrl: raw.apiBaseUrl.replace(/\/+$/, ''),
    msal: {
      clientId,
      authority: raw.msalTenantId ? `https://login.microsoftonline.com/${raw.msalTenantId}` : '',
    },
    apiScope: raw.apiScope || (clientId ? `api://${clientId}/access_as_user` : ''),
    demoSeedUrl: raw.demoSeedUrl?.trim() || '',
  };
}

export const isDemo = (config: AppConfig): boolean => !config.apiBaseUrl;

export function demoEntryRequested(search: string): boolean {
  return new URLSearchParams(search).get('demo') === '1';
}

export function entryUrl(href: string, demo: boolean): string {
  const url = new URL(href);
  if (demo) url.searchParams.set('demo', '1');
  else url.searchParams.delete('demo');
  url.hash = '/';
  return url.href;
}

/** Things that are missing when the app is pointed at a backend but can't sign anyone in. Empty = fine. */
export function configProblems(config: AppConfig): string[] {
  const problems: string[] = [];
  if (config.demoSeedUrl) {
    try {
      const url = new URL(config.demoSeedUrl);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash) throw new Error();
    } catch {
      problems.push('BUN_PUBLIC_DEMO_SEED_URL must be an absolute HTTPS URL without credentials or a fragment.');
    }
  }
  if (isDemo(config)) return problems;
  if (!config.msal.clientId) problems.push('BUN_PUBLIC_MSAL_CLIENT_ID is not set.');
  if (!config.msal.authority) problems.push('BUN_PUBLIC_MSAL_TENANT_ID is not set.');
  return problems;
}
